/**
 * Child Service Report Dialog Component
 * Embedded form for documenting child developmental progress
 * Opens in a dialog within ChildrenRecordsTab for full-time children
 */
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Printer, Save, RotateCcw, FileText, PenTool, FolderOpen, Trash2, ChevronDown, ChevronRight, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Signature font options
const SIGNATURE_FONTS = [
  { value: "cursive", label: "Cursive", fontFamily: "'Brush Script MT', 'Segoe Script', cursive" },
  { value: "dancing", label: "Dancing Script", fontFamily: "'Dancing Script', cursive" },
  { value: "pacifico", label: "Pacifico", fontFamily: "'Pacifico', cursive" },
  { value: "greatvibes", label: "Great Vibes", fontFamily: "'Great Vibes', cursive" },
  { value: "sacramento", label: "Sacramento", fontFamily: "'Sacramento', cursive" },
  { value: "allura", label: "Allura", fontFamily: "'Allura', cursive" },
  { value: "normal", label: "Normal", fontFamily: "Arial, sans-serif" },
];

// Storage key for localStorage - kept for backwards compatibility
const STORAGE_KEY = 'childServiceReports';

const ChildServiceReportDialog = ({ 
  open, 
  onOpenChange, 
  childId, 
  childName, 
  childBirthdate,
  childPickupPersons,
  onSaved 
}) => {
  const { user, organization } = useAuth();
  const { language } = useLanguage();
  
  const [signatureFont, setSignatureFont] = useState("cursive");
  const [savedReports, setSavedReports] = useState([]);
  const [showSavedReports, setShowSavedReports] = useState(false);
  const [savingReport, setSavingReport] = useState(false);
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Form data state
  const [formData, setFormData] = useState({
    childName: "",
    facilityName: "",
    birthDate: "",
    strengthsPhysical: "",
    strengthsKnowledge: "",
    strengthsSocialEmotional: "",
    strengthsCommunication: "",
    milestonesPhysical: "",
    milestonesKnowledge: "",
    milestonesSocialEmotional: "",
    milestonesCommunication: "",
    atHomePhysical: "",
    atHomeKnowledge: "",
    atHomeSocialEmotional: "",
    atHomeCommunication: "",
    facilityReporterSignature: "",
    facilityReporterName: "",
    facilityReporterDate: new Date().toISOString().split('T')[0],
    parentGuardianSignature: "",
    parentGuardianName: "",
    parentGuardianDate: ""
  });

  // Get the selected font family
  const getSignatureFontFamily = () => {
    const font = SIGNATURE_FONTS.find(f => f.value === signatureFont);
    return font ? font.fontFamily : "'Brush Script MT', cursive";
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData(prev => ({
        ...prev,
        childName: childName || "",
        facilityName: organization?.name || "",
        birthDate: childBirthdate ? formatDate(childBirthdate) : "",
        facilityReporterName: user?.name || "",
        facilityReporterDate: new Date().toISOString().split('T')[0]
      }));
      // Fetch saved reports and auto-load the latest one
      fetchSavedReportsForChild(true);
    }
  }, [open, childName, childBirthdate, organization, user]);

  // Load saved reports for this specific child from API
  const fetchSavedReportsForChild = async (autoLoad = false) => {
    try {
      const response = await axios.get(`${API}/child-service-reports/by-child/${childId}`);
      const reports = response.data || [];
      setSavedReports(reports);
      // Auto-load the most recent report when opening
      if (autoLoad && reports.length > 0) {
        const latest = reports.sort((a, b) => new Date(b.savedAt || b.createdAt) - new Date(a.savedAt || a.createdAt))[0];
        handleLoadReport(latest, true);
      }
    } catch (error) {
      console.error("Error loading saved reports:", error);
      // Fallback to localStorage if API fails
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        const reports = stored ? JSON.parse(stored) : [];
        const childReports = reports.filter(r => r.childId === childId);
        setSavedReports(childReports);
      } catch (e) {
        console.error("Error loading from localStorage:", e);
      }
    }
  };

  // Save report to API
  const handleSaveReport = async () => {
    if (!formData.childName) {
      toast.error(language === 'es' ? "Nombre del niño requerido" : "Child name is required");
      return;
    }
    
    if (!formData.facilityReporterDate) {
      toast.error(language === 'es' ? "Por favor ingrese la fecha en la sección de firma" : "Please enter the date in the signature section");
      return;
    }
    
    try {
      setSavingReport(true);
      
      // Create report data
      const reportData = {
        childId,
        childName: formData.childName,
        facilityName: formData.facilityName,
        birthDate: formData.birthDate,
        strengthsPhysical: formData.strengthsPhysical,
        strengthsKnowledge: formData.strengthsKnowledge,
        strengthsSocialEmotional: formData.strengthsSocialEmotional,
        strengthsCommunication: formData.strengthsCommunication,
        milestonesPhysical: formData.milestonesPhysical,
        milestonesKnowledge: formData.milestonesKnowledge,
        milestonesSocialEmotional: formData.milestonesSocialEmotional,
        milestonesCommunication: formData.milestonesCommunication,
        atHomePhysical: formData.atHomePhysical,
        atHomeKnowledge: formData.atHomeKnowledge,
        atHomeSocialEmotional: formData.atHomeSocialEmotional,
        atHomeCommunication: formData.atHomeCommunication,
        facilityReporterSignature: formData.facilityReporterSignature,
        facilityReporterName: formData.facilityReporterName,
        facilityReporterDate: formData.facilityReporterDate,
        parentGuardianSignature: formData.parentGuardianSignature,
        parentGuardianName: formData.parentGuardianName,
        parentGuardianDate: formData.parentGuardianDate,
        signatureFont: signatureFont
      };
      
      const response = await axios.post(`${API}/child-service-reports`, reportData);
      const savedYear = response.data.year || new Date().getFullYear();
      
      toast.success(language === 'es' ? `Informe guardado en ${savedYear}` : `Report saved to ${savedYear}`);
      fetchSavedReportsForChild();
      onSaved?.();
    } catch (error) {
      console.error("Error saving report:", error);
      toast.error(language === 'es' ? "Error al guardar el informe" : "Error saving report");
    } finally {
      setSavingReport(false);
    }
  };

  // Normalize date to ISO format (handles both "6/1/2025" and "2025-06-01")
  const normalizeDate = (dateStr) => {
    if (!dateStr) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    try {
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) {
        return d.toISOString().split('T')[0];
      }
    } catch (e) {}
    return dateStr;
  };

  // Load a saved report
  const handleLoadReport = (report, silent = false) => {
    setSignatureFont(report.signatureFont || "cursive");
    setFormData({
      childName: report.childName || childName || "",
      facilityName: report.facilityName || organization?.name || "",
      birthDate: report.birthDate || "",
      strengthsPhysical: report.strengthsPhysical || "",
      strengthsKnowledge: report.strengthsKnowledge || "",
      strengthsSocialEmotional: report.strengthsSocialEmotional || "",
      strengthsCommunication: report.strengthsCommunication || "",
      milestonesPhysical: report.milestonesPhysical || "",
      milestonesKnowledge: report.milestonesKnowledge || "",
      milestonesSocialEmotional: report.milestonesSocialEmotional || "",
      milestonesCommunication: report.milestonesCommunication || "",
      atHomePhysical: report.atHomePhysical || "",
      atHomeKnowledge: report.atHomeKnowledge || "",
      atHomeSocialEmotional: report.atHomeSocialEmotional || "",
      atHomeCommunication: report.atHomeCommunication || "",
      facilityReporterSignature: report.facilityReporterSignature || "",
      facilityReporterName: report.facilityReporterName || "",
      facilityReporterDate: normalizeDate(report.facilityReporterDate),
      parentGuardianSignature: report.parentGuardianSignature || "",
      parentGuardianName: report.parentGuardianName || "",
      parentGuardianDate: normalizeDate(report.parentGuardianDate)
    });
    setShowSavedReports(false);
    if (!silent) toast.success(language === 'es' ? "Informe cargado" : "Report loaded");
  };

  // Delete a saved report from API
  const handleDeleteReport = async (reportId, e) => {
    e.stopPropagation();
    if (!window.confirm(language === 'es' ? "¿Eliminar este informe?" : "Delete this report?")) return;
    
    try {
      await axios.delete(`${API}/child-service-reports/${reportId}`);
      toast.success(language === 'es' ? "Informe eliminado" : "Report deleted");
      fetchSavedReportsForChild();
    } catch (error) {
      console.error("Error deleting report:", error);
      toast.error(language === 'es' ? "Error al eliminar" : "Error deleting report");
    }
  };

  // Open email confirmation with pre-filled parent email
  const handleOpenEmailDialog = () => {
    // Get the current report ID from saved reports
    if (savedReports.length === 0) {
      toast.error("Please save the report first before emailing");
      return;
    }
    const parentEmail = childPickupPersons?.find(p => p.email)?.email || "";
    setEmailAddress(parentEmail);
    setShowEmailConfirm(true);
  };

  // Send the report via email
  const handleSendEmail = async () => {
    if (!emailAddress.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    const latestReport = savedReports.sort((a, b) => new Date(b.savedAt || b.createdAt) - new Date(a.savedAt || a.createdAt))[0];
    if (!latestReport) return;

    setSendingEmail(true);
    try {
      await axios.post(`${API}/child-service-reports/${latestReport.id}/email-parent`, {
        email: emailAddress.trim()
      });
      toast.success(`Report emailed to ${emailAddress}`);
      setShowEmailConfirm(false);
    } catch (error) {
      console.error("Error emailing report:", error);
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Reset form
  const handleReset = () => {
    setFormData({
      childName: childName || "",
      facilityName: organization?.name || "",
      birthDate: childBirthdate ? formatDate(childBirthdate) : "",
      strengthsPhysical: "",
      strengthsKnowledge: "",
      strengthsSocialEmotional: "",
      strengthsCommunication: "",
      milestonesPhysical: "",
      milestonesKnowledge: "",
      milestonesSocialEmotional: "",
      milestonesCommunication: "",
      atHomePhysical: "",
      atHomeKnowledge: "",
      atHomeSocialEmotional: "",
      atHomeCommunication: "",
      facilityReporterSignature: "",
      facilityReporterName: user?.name || "",
      facilityReporterDate: new Date().toISOString().split('T')[0],
      parentGuardianSignature: "",
      parentGuardianName: "",
      parentGuardianDate: ""
    });
  };

  // Print the form
  const handlePrint = () => {
    const signatureFontFamily = getSignatureFontFamily();
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Child Service Report - ${formData.childName || 'Form'}</title>
          <link rel="preconnect" href="https://fonts.googleapis.com">
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
          <link href="https://fonts.googleapis.com/css2?family=Allura&family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Pacifico&family=Sacramento&display=swap" rel="stylesheet">
          <style>
            @page { size: 8.5in 11in; margin: 0.5in; }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; font-size: 11px; line-height: 1.4; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            .form-container { max-width: 8in; margin: 0 auto; padding: 20px; }
            .header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #000; padding-bottom: 10px; }
            .header h1 { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
            .header-row { display: flex; justify-content: space-between; margin-top: 10px; gap: 20px; }
            .header-field { flex: 1; }
            .header-field label { font-weight: bold; }
            .header-field .value { border-bottom: 1px solid #000; min-height: 18px; padding: 2px 5px; }
            .section { margin-bottom: 15px; }
            .section-title { font-weight: bold; font-size: 12px; margin-bottom: 8px; background: #f0f0f0; padding: 5px; }
            .field-row { margin-bottom: 10px; }
            .field-label { font-weight: bold; font-size: 10px; margin-bottom: 3px; }
            .field-value { border: 1px solid #ccc; min-height: 50px; padding: 5px; white-space: pre-wrap; }
            .signature-section { margin-top: 20px; padding-top: 15px; border-top: 1px solid #000; }
            .signature-row { display: flex; gap: 20px; margin-bottom: 10px; }
            .signature-field { flex: 1; }
            .signature-field label { font-weight: bold; font-size: 10px; }
            .signature-field .value { border-bottom: 1px solid #000; min-height: 25px; margin-top: 5px; padding: 2px 5px; }
            .signature-value { font-family: ${signatureFontFamily}; font-size: 18px; color: #1a365d; }
            @media print { .no-print { display: none !important; } }
          </style>
        </head>
        <body>
          <div class="form-container">
            <div class="header">
              <h1>CHILD SERVICE REPORT</h1>
              <div class="header-row">
                <div class="header-field"><label>Child's Name:</label><div class="value">${formData.childName}</div></div>
                <div class="header-field"><label>Facility Name:</label><div class="value">${formData.facilityName}</div></div>
                <div class="header-field"><label>Birth Date:</label><div class="value">${formData.birthDate}</div></div>
              </div>
            </div>
            <div class="section">
              <div class="section-title">Your child's strengths, as age appropriate, in the following areas are:</div>
              <div class="field-row"><div class="field-label">Physical (fine motor and gross motor):</div><div class="field-value">${formData.strengthsPhysical}</div></div>
              <div class="field-row"><div class="field-label">Knowledge and Skills (approaches to learning, math, science and social studies):</div><div class="field-value">${formData.strengthsKnowledge}</div></div>
              <div class="field-row"><div class="field-label">Social Emotional (personal-social):</div><div class="field-value">${formData.strengthsSocialEmotional}</div></div>
              <div class="field-row"><div class="field-label">Communication, Language and Literacy:</div><div class="field-value">${formData.strengthsCommunication}</div></div>
            </div>
            <div class="section">
              <div class="section-title">The next developmental milestones, as age appropriate, we're working on are:</div>
              <div class="field-row"><div class="field-label">Physical (fine motor and gross motor):</div><div class="field-value">${formData.milestonesPhysical}</div></div>
              <div class="field-row"><div class="field-label">Knowledge and Skills (approaches to learning, math, science and social studies):</div><div class="field-value">${formData.milestonesKnowledge}</div></div>
              <div class="field-row"><div class="field-label">Social Emotional (personal-social):</div><div class="field-value">${formData.milestonesSocialEmotional}</div></div>
              <div class="field-row"><div class="field-label">Communication, Language and Literacy:</div><div class="field-value">${formData.milestonesCommunication}</div></div>
            </div>
            <div class="signature-section">
              <div class="section-title">Facility person who completed this child's report:</div>
              <div class="signature-row">
                <div class="signature-field"><label>Signature:</label><div class="value"><span class="signature-value">${formData.facilityReporterSignature}</span></div></div>
                <div class="signature-field"><label>Name:</label><div class="value">${formData.facilityReporterName}</div></div>
                <div class="signature-field"><label>Date:</label><div class="value">${formData.facilityReporterDate ? new Date(formData.facilityReporterDate + 'T00:00:00').toLocaleDateString('en-US', {month:'2-digit', day:'2-digit', year:'numeric'}) : ''}</div></div>
              </div>
            </div>
            <div class="signature-section">
              <div class="section-title">This report was reviewed with and a copy given to the following parent/guardian:</div>
              <div class="signature-row">
                <div class="signature-field"><label>Signature:</label><div class="value"><span class="signature-value">${formData.parentGuardianSignature}</span></div></div>
                <div class="signature-field"><label>Name:</label><div class="value">${formData.parentGuardianName}</div></div>
                <div class="signature-field"><label>Date:</label><div class="value">${formData.parentGuardianDate ? new Date(formData.parentGuardianDate + 'T00:00:00').toLocaleDateString('en-US', {month:'2-digit', day:'2-digit', year:'numeric'}) : ''}</div></div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="child-service-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-600" />
            {language === 'es' ? "Informe de Servicio del Niño" : "Child Service Report"}
            <span className="text-sm font-normal text-gray-500">- {childName}</span>
          </DialogTitle>
          <DialogDescription>
            {language === 'es' 
              ? "Complete el informe de desarrollo para este niño"
              : "Complete the developmental progress report for this child"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Header Actions */}
          <div className="flex flex-wrap items-center gap-2 pb-2 border-b">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSavedReports(!showSavedReports)}
              data-testid="toggle-saved-reports-btn"
            >
              <FolderOpen className="w-4 h-4 mr-1" />
              {language === 'es' ? "Informes Guardados" : "Saved Reports"} ({savedReports.length})
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              data-testid="reset-form-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              {language === 'es' ? "Limpiar" : "Clear"}
            </Button>
            <Button
              size="sm"
              onClick={handleSaveReport}
              disabled={savingReport}
              className="bg-green-600 hover:bg-green-700"
              data-testid="save-report-btn"
            >
              <Save className="w-4 h-4 mr-1" />
              {savingReport ? "..." : (language === 'es' ? "Guardar" : "Save")}
            </Button>
            <Button
              size="sm"
              onClick={handlePrint}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="print-report-btn"
            >
              <Printer className="w-4 h-4 mr-1" />
              {language === 'es' ? "Imprimir" : "Print"}
            </Button>
          </div>

          {/* Saved Reports Panel */}
          {showSavedReports && (
            <div className="bg-gray-50 border rounded-lg p-3">
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                <FolderOpen className="w-4 h-4" />
                {language === 'es' ? "Informes Guardados para este Niño" : "Saved Reports for this Child"}
              </h4>
              {savedReports.length === 0 ? (
                <p className="text-gray-500 text-sm">
                  {language === 'es' ? "No hay informes guardados" : "No saved reports yet"}
                </p>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {savedReports.sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt)).map(report => (
                    <div
                      key={report.id}
                      onClick={() => handleLoadReport(report)}
                      className="p-2 bg-white rounded border hover:bg-blue-50 cursor-pointer flex items-center justify-between"
                    >
                      <div>
                        <span className="text-sm font-medium">
                          {new Date(report.savedAt).toLocaleDateString()}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">
                          by {report.savedBy || 'Unknown'}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleDeleteReport(report.id, e)}
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Google Fonts for signatures */}
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
          <link href="https://fonts.googleapis.com/css2?family=Allura&family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Pacifico&family=Sacramento&display=swap" rel="stylesheet" />

          {/* Form Header Fields */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">{language === 'es' ? "Nombre del Niño" : "Child's Name"}</Label>
              <Input
                value={formData.childName}
                onChange={(e) => handleChange('childName', e.target.value)}
                className="h-8 text-sm"
                data-testid="child-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{language === 'es' ? "Nombre del Centro" : "Facility Name"}</Label>
              <Input
                value={formData.facilityName}
                onChange={(e) => handleChange('facilityName', e.target.value)}
                className="h-8 text-sm"
                data-testid="facility-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{language === 'es' ? "Fecha de Nacimiento" : "Birth Date"}</Label>
              <Input
                value={formData.birthDate}
                onChange={(e) => handleChange('birthDate', e.target.value)}
                placeholder="MM/DD/YYYY"
                className="h-8 text-sm"
                data-testid="birth-date-input"
              />
            </div>
          </div>

          {/* Section 1: Child's Strengths */}
          <div className="bg-green-50 rounded-lg p-3 border border-green-200">
            <h4 className="font-medium text-green-800 text-sm mb-2">
              {language === 'es' 
                ? "Las fortalezas de su hijo, según su edad:"
                : "Your child's strengths, as age appropriate:"}
            </h4>
            <div className="space-y-2">
              <div>
                <Label className="text-xs text-gray-600">{language === 'es' ? "Físico (motricidad):" : "Physical (fine/gross motor):"}</Label>
                <Textarea value={formData.strengthsPhysical} onChange={(e) => handleChange('strengthsPhysical', e.target.value)} rows={2} className="text-sm" data-testid="strengths-physical" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">{language === 'es' ? "Conocimientos y Habilidades:" : "Knowledge and Skills:"}</Label>
                <Textarea value={formData.strengthsKnowledge} onChange={(e) => handleChange('strengthsKnowledge', e.target.value)} rows={2} className="text-sm" data-testid="strengths-knowledge" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">{language === 'es' ? "Social Emocional:" : "Social Emotional:"}</Label>
                <Textarea value={formData.strengthsSocialEmotional} onChange={(e) => handleChange('strengthsSocialEmotional', e.target.value)} rows={2} className="text-sm" data-testid="strengths-social" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">{language === 'es' ? "Comunicación y Lenguaje:" : "Communication, Language:"}</Label>
                <Textarea value={formData.strengthsCommunication} onChange={(e) => handleChange('strengthsCommunication', e.target.value)} rows={2} className="text-sm" data-testid="strengths-communication" />
              </div>
            </div>
          </div>

          {/* Section 2: Developmental Milestones */}
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
            <h4 className="font-medium text-blue-800 text-sm mb-2">
              {language === 'es'
                ? "Próximos hitos del desarrollo:"
                : "Next developmental milestones we're working on:"}
            </h4>
            <div className="space-y-2">
              <div>
                <Label className="text-xs text-gray-600">{language === 'es' ? "Físico (motricidad):" : "Physical (fine/gross motor):"}</Label>
                <Textarea value={formData.milestonesPhysical} onChange={(e) => handleChange('milestonesPhysical', e.target.value)} rows={2} className="text-sm" data-testid="milestones-physical" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">{language === 'es' ? "Conocimientos y Habilidades:" : "Knowledge and Skills:"}</Label>
                <Textarea value={formData.milestonesKnowledge} onChange={(e) => handleChange('milestonesKnowledge', e.target.value)} rows={2} className="text-sm" data-testid="milestones-knowledge" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">{language === 'es' ? "Social Emocional:" : "Social Emotional:"}</Label>
                <Textarea value={formData.milestonesSocialEmotional} onChange={(e) => handleChange('milestonesSocialEmotional', e.target.value)} rows={2} className="text-sm" data-testid="milestones-social" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">{language === 'es' ? "Comunicación y Lenguaje:" : "Communication, Language:"}</Label>
                <Textarea value={formData.milestonesCommunication} onChange={(e) => handleChange('milestonesCommunication', e.target.value)} rows={2} className="text-sm" data-testid="milestones-communication" />
              </div>
            </div>
          </div>

          {/* Signatures Section */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-800 text-sm">{language === 'es' ? "Firmas" : "Signatures"}</h4>
              <div className="flex items-center gap-2">
                <PenTool className="w-4 h-4 text-gray-500" />
                <Label className="text-xs text-gray-600">{language === 'es' ? "Estilo:" : "Style:"}</Label>
                <Select value={signatureFont} onValueChange={setSignatureFont}>
                  <SelectTrigger className="w-32 h-7 text-xs" data-testid="signature-font-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SIGNATURE_FONTS.map(font => (
                      <SelectItem key={font.value} value={font.value}>
                        <span style={{ fontFamily: font.fontFamily }}>{font.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Facility Reporter */}
            <div className="mb-3">
              <Label className="text-xs font-medium text-gray-700">
                {language === 'es' ? "Persona del centro:" : "Facility person who completed this report:"}
              </Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <div>
                  <Label className="text-xs text-gray-500">{language === 'es' ? "Firma" : "Signature"}</Label>
                  <Input
                    value={formData.facilityReporterSignature}
                    onChange={(e) => handleChange('facilityReporterSignature', e.target.value)}
                    placeholder={language === 'es' ? "Escriba su nombre..." : "Type your name..."}
                    className="h-10 text-lg text-blue-900"
                    style={{ fontFamily: getSignatureFontFamily() }}
                    data-testid="reporter-signature"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">{language === 'es' ? "Nombre" : "Name"}</Label>
                  <Input value={formData.facilityReporterName} onChange={(e) => handleChange('facilityReporterName', e.target.value)} className="h-8 text-sm" data-testid="reporter-name" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">{language === 'es' ? "Fecha" : "Date"}</Label>
                  <Input
                    type="date"
                    value={formData.facilityReporterDate || ''}
                    onChange={(e) => handleChange('facilityReporterDate', e.target.value)}
                    className="h-8 text-sm"
                    data-testid="reporter-date"
                  />
                </div>
              </div>
            </div>

            {/* Parent/Guardian */}
            <div>
              <Label className="text-xs font-medium text-gray-700">
                {language === 'es' ? "Padre/tutor que revisó:" : "Parent/guardian who reviewed:"}
              </Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <div>
                  <Label className="text-xs text-gray-500">{language === 'es' ? "Firma" : "Signature"}</Label>
                  <Input
                    value={formData.parentGuardianSignature}
                    onChange={(e) => handleChange('parentGuardianSignature', e.target.value)}
                    placeholder={language === 'es' ? "Escriba su nombre..." : "Type your name..."}
                    className="h-10 text-lg text-blue-900"
                    style={{ fontFamily: getSignatureFontFamily() }}
                    data-testid="parent-signature"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">{language === 'es' ? "Nombre" : "Name"}</Label>
                  <Input value={formData.parentGuardianName} onChange={(e) => handleChange('parentGuardianName', e.target.value)} className="h-8 text-sm" data-testid="parent-name" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">{language === 'es' ? "Fecha" : "Date"}</Label>
                  <Input
                    type="date"
                    value={formData.parentGuardianDate || ''}
                    onChange={(e) => handleChange('parentGuardianDate', e.target.value)}
                    className="h-8 text-sm"
                    data-testid="parent-date"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {showEmailConfirm && (
          <div className="mx-4 mb-2 p-4 bg-teal-50 border border-teal-200 rounded-lg">
            <p className="text-sm font-medium text-teal-800 mb-2">Email Report to Parent</p>
            <p className="text-sm text-gray-600 mb-2">
              Send the Child Service Report for <strong>{formData.childName}</strong> to:
            </p>
            <input
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="Enter parent email address"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
              data-testid="email-parent-input"
            />
            <p className="text-xs text-orange-600 mb-3">Are you sure you want to send this report?</p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowEmailConfirm(false)}>Cancel</Button>
              <Button
                size="sm"
                onClick={handleSendEmail}
                disabled={sendingEmail || !emailAddress.trim()}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="confirm-send-email"
              >
                {sendingEmail ? "Sending..." : "Yes, Send Email"}
              </Button>
            </div>
          </div>
        )}

        <div className="px-4 pb-1">
          <p className="text-xs text-orange-600 text-center">You must save before closing this form</p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {language === 'es' ? "Cerrar" : "Close"}
          </Button>
          <Button onClick={handleSaveReport} className="bg-green-600 hover:bg-green-700" data-testid="save-report-footer">
            <Save className="w-4 h-4 mr-1" />
            {language === 'es' ? "Guardar" : "Save Report"}
          </Button>
          <Button onClick={handleOpenEmailDialog} className="bg-teal-600 hover:bg-teal-700" data-testid="email-parent-btn">
            <Mail className="w-4 h-4 mr-1" />
            Email to Parent
          </Button>
          <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
            <Printer className="w-4 h-4 mr-1" />
            {language === 'es' ? "Imprimir" : "Print Report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChildServiceReportDialog;
